///
/// EarthBrowser
///
/// Computed pressure field texture frame (from EBWX grid data, not CDN PNG).
/// Uploads a single-channel R32F float texture (pressure in hPa, 0 = invalid)
/// for GPU isoline rendering.
///
/// The initial int16 field/flow data from the compute worker is held only
/// until the layer persists it to the local sqlite database (markPersisted).
/// After that no CPU copy is retained: when the windowed texture series
/// purges this frame's GPU textures and later needs them again, load()
/// re-fetches the blobs from the database asynchronously.
///
/// Created by Matt Giger on 4/12/2026
/// Copyright (c) 2026 Geodata Labs
///

import { type time, type timedelta } from "@stovokor/math/time"
import { ComponentType, InternalFormat, PixelFormat, TextureFilter, TextureWrap } from "@stovokor/shader/GLConstants"
import { Texture } from "@stovokor/shader/Texture"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { PressureFieldTextureFrame } from "./PressureFieldTextureFrame"
import { SynopticTable } from "./SynopticTable"
import { dequantizeField } from "./SynopticFieldCodec"

export class PressureFieldComputedFrame extends PressureFieldTextureFrame {
	private fieldData?: Int16Array
	private textureWidth: number
	private textureHeight: number
	private flowData?: Float32Array
	private flowWidth = 0
	private flowHeight = 0
	private persisted = false
	/** Invalidates in-flight database fetches on purge/re-load */
	private fetchToken = 0

	constructor(
		context: StovokorContext,
		fieldData: Int16Array,
		width: number,
		height: number,
		startTime: time,
		duration: timedelta,
		frameId: string
	) {
		super(context, frameId, startTime, duration)
		this.fieldData = fieldData
		this.textureWidth = width
		this.textureHeight = height
	}

	/** Attach the displacement field toward the next frame (interleaved RG,
	 *  UV units). Must be called before load(). */
	public setFlow(data: Float32Array, width: number, height: number): void {
		this.flowData = data
		this.flowWidth = width
		this.flowHeight = height
	}

	/** The database row for this frame is now queryable; drop the initial
	 *  CPU copies. Loads and re-loads fetch from sqlite from here on. */
	public markPersisted(): void {
		this.persisted = true
		this.fieldData = undefined
		this.flowData = undefined
	}

	public override load(): void {
		if (this.loading || this.available) return

		// Initial data still in hand (persistence pending): upload synchronously
		if (this.fieldData) {
			this.uploadField(this.fieldData, this.textureWidth, this.textureHeight,
				this.flowData ?? null, this.flowWidth, this.flowHeight)
			return
		}

		if (!this.persisted) return

		this.loading = true
		const token = ++this.fetchToken
		SynopticTable.getPressureField(this.url)
			.then(row => {
				if (token !== this.fetchToken || !row) return
				const field = new Int16Array(blobBuffer(row.field))
				const flow = row.flow ? new Float32Array(blobBuffer(row.flow)) : null
				this.uploadField(field, row.width, row.height, flow, row.flow_width, row.flow_height)
			})
			.catch(e => {
				console.warn(`[PressureFieldComputedFrame] DB fetch failed for ${this.url}`, e)
			})
			.finally(() => {
				if (token === this.fetchToken) this.loading = false
			})
	}

	public override purge(): void {
		this.fetchToken++
		this.flowTexture?.destroy()
		this.flowTexture = undefined
		super.purge()
	}

	private uploadField(
		field: Int16Array,
		width: number,
		height: number,
		flow: Float32Array | null,
		flowWidth: number,
		flowHeight: number
	): void {
		this.createNewTexture()
		this.texture.uploadArrayBuffer(dequantizeField(field), width, height, 1, 32)

		// Float textures default to NEAREST; linear filtering of floats needs
		// the (near universally supported) extension. No mipmaps: the shader
		// fades lines by screen-space density when minified.
		const linearOk = this.context.hasExtension('OES_texture_float_linear')
		if (linearOk) {
			this.texture.setFilters(TextureFilter.Linear, TextureFilter.Linear)
		}

		if (flow && flowWidth > 0 && flowHeight > 0) {
			this.uploadFlowTexture(flow, flowWidth, flowHeight, linearOk)
		}

		this.available = true
	}

	/** Two-channel float upload with the filter and wrap settings applied */
	private uploadFlowTexture(data: Float32Array, width: number, height: number, linear: boolean): void {
		const tex = new Texture(this.context)
		const filter = linear ? TextureFilter.Linear : TextureFilter.Nearest
		tex.minFilter = filter
		tex.magFilter = filter
		tex.wrapS = TextureWrap.Repeat
		tex.wrapT = TextureWrap.ClampToEdge
		tex.uploadRawData(data, width, height, InternalFormat.RG32F, PixelFormat.RG, ComponentType.Float)
		this.flowTexture = tex
	}

	protected override createNewTexture(): void {
		this.texture = new Texture(
			this.context,
			1,
			1,
			InternalFormat.R32F,
			PixelFormat.Red,
			ComponentType.Float,
			TextureFilter.Linear,
			TextureFilter.Linear,
			TextureWrap.Repeat,
			TextureWrap.ClampToEdge
		)
	}
}

/** Typed-array-safe buffer from a blob column value: wa-sqlite blob copies
 *  start at offset 0, but slice defensively if this one doesn't */
function blobBuffer(blob: Uint8Array): ArrayBuffer {
	if (blob.byteOffset === 0 && blob.byteLength === blob.buffer.byteLength) {
		return blob.buffer as ArrayBuffer
	}
	return blob.buffer.slice(blob.byteOffset, blob.byteOffset + blob.byteLength) as ArrayBuffer
}
